import { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import {
  CREATE_SLOT_HOLD,
  RELEASE_SLOT_HOLD,
  paymentModeRefusalFrom,
  slotConflictFrom,
  type CreateSlotHoldData,
  type SlotConflict,
  type SlotHold,
} from "./operations";
import { ensureGuestIdentity } from "../../lib/firebase";
import { claimGuestIdentity } from "../../lib/guestSession";

export type HoldState =
  | { status: "none" }
  | { status: "acquiring" }
  | {
      status: "held";
      hold: SlotHold;
      secondsLeft: number;
      /**
       * The uid the hold was requested under. Backend owns a hold by the
       * verified uid on the token, so a hold is only this client's to commit
       * while that uid is current (security audit L1, 2026-09-27).
       */
      ownerUid?: string;
    }
  | { status: "expired"; startTime: string }
  | { status: "conflict"; conflict: Exclude<SlotConflict, null>; startTime: string }
  | { status: "failed"; startTime: string }
  /**
   * A payment-mode refusal (`GUEST_PAYMENT_MODE_NOT_AVAILABLE` for a guest, or
   * `IN_APP_PAYMENTS_NOT_AVAILABLE`). Not a connection problem and not the
   * slot: this professional cannot be held for this client, whatever time.
   */
  | { status: "refused"; detailCode: string; startTime: string };

function secondsUntil(expiresAt: string): number {
  return Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000));
}

/**
 * The slot hold, and the countdown against it.
 *
 * THE COUNTDOWN IS SERVER-AUTHORITATIVE. It is computed from the hold's
 * `expiresAt` on every tick, not decremented from a number seeded at
 * acquisition. Product's contract: a re-hold RESUMES the existing expiration
 * and must not restart it — so a client-seeded timer would show a fresh ten
 * minutes for a hold with two left, and the slot would vanish under someone
 * watching a countdown that said they had time.
 *
 * Recomputing also survives a backgrounded tab. `setInterval` does not fire
 * while a phone is locked, and a decrementing counter would resume from where
 * it paused — showing time that is already gone.
 */
export function useHold() {
  const client = useApolloClient();
  const [state, setState] = useState<HoldState>({ status: "none" });

  // Kept in a ref so the unmount cleanup can reach the CURRENT hold without
  // making the effect depend on it — a dependency there would release and
  // re-acquire on every tick.
  const activeHold = useRef<SlotHold | null>(null);
  // A day/time change or unmount can happen while createSlotHold is awaiting
  // identity or the network. Only the latest request may become visible; a
  // late successful hold must be released rather than orphaned for its TTL.
  const requestVersion = useRef(0);
  // A sign-in must not replace the guest UID while an earlier hold creation is
  // still on the wire. Otherwise its late release would carry the new UID and
  // fail authorization, leaving the guest's slot held until backend expiry.
  const pendingAcquires = useRef(new Set<Promise<void>>());
  // A release still on the wire. The Backend answers createSlotHold for the
  // same owner and window with that owner's EXISTING hold, so a new hold asked
  // for before an earlier release lands can be handed the very hold that
  // release then deletes — and the client meets hold-gone again. A new
  // acquisition waits for it (review A3 on the hold-gone fix, 2026-09-26).
  const pendingRelease = useRef<Promise<void> | null>(null);

  const releaseSlot = useCallback(async (holdId: string) => {
    // A release is authorized by the owning UID's token, so the identity is
    // claimed until it lands: a rotation in between would send it unsigned.
    const unclaim = claimGuestIdentity();
    try {
      await client.mutate({ mutation: RELEASE_SLOT_HOLD, variables: { holdId } });
    } catch {
      // Release is best effort. The Backend expiry remains authoritative; do
      // not imply a failed network response proves the hold is still active.
    } finally {
      unclaim();
    }
  }, [client]);

  const release = useCallback(async () => {
    requestVersion.current += 1;
    const inFlight = [...pendingAcquires.current];
    const hold = activeHold.current;
    activeHold.current = null;
    setState({ status: "none" });
    if (hold) {
      const releasing = releaseSlot(hold.id);
      pendingRelease.current = releasing;
      await releasing;
      if (pendingRelease.current === releasing) pendingRelease.current = null;
    }
    // The invalidated acquisition releases any late result using the old
    // identity before this promise resolves and Book enables sign-in.
    await Promise.all(inFlight);
  }, [releaseSlot]);

  const acquireInternal = useCallback(
    async (input: { professionalId: string; serviceId: string; startTime: string }) => {
      const version = ++requestVersion.current;
      const previous = activeHold.current;
      activeHold.current = null;
      setState({ status: "acquiring" });
      try {
        // A second time choice must not overwrite the only ref to an earlier
        // hold. Wait for the best-effort release before asking for another.
        if (previous) await releaseSlot(previous.id);
        if (pendingRelease.current) await pendingRelease.current;
        if (version !== requestVersion.current) return;
        // A hold is owned by the verified uid on the token, so a guest identity
        // has to exist BEFORE the mutation — not after it fails.
        const owner = await ensureGuestIdentity();
        if (version !== requestVersion.current) return;

        const result = await client.mutate<CreateSlotHoldData>({
          mutation: CREATE_SLOT_HOLD,
          variables: { input },
        });

        const hold = result.data?.createSlotHold;
        if (version !== requestVersion.current) {
          if (hold) await releaseSlot(hold.id);
          return;
        }
        if (!hold) {
          setState({ status: "failed", startTime: input.startTime });
          return;
        }

        activeHold.current = hold;
        setState({ status: "held", hold, secondsLeft: secondsUntil(hold.expiresAt), ownerUid: owner.uid });
      } catch (error) {
        if (version !== requestVersion.current) return;
        const refusal = paymentModeRefusalFrom(error);
        if (refusal) {
          setState({ status: "refused", detailCode: refusal, startTime: input.startTime });
          return;
        }
        const conflict = slotConflictFrom(error);
        if (conflict) {
          setState({ status: "conflict", conflict, startTime: input.startTime });
          return;
        }
        setState({ status: "failed", startTime: input.startTime });
      }
    },
    [client, releaseSlot]
  );

  const acquire = useCallback(
    (input: { professionalId: string; serviceId: string; startTime: string }) => {
      const running = acquireInternal(input);
      pendingAcquires.current.add(running);
      void running.finally(() => {
        pendingAcquires.current.delete(running);
      });
      return running;
    },
    [acquireInternal]
  );

  // Tick the countdown. Recomputed from expiresAt each second rather than
  // decremented, for the reasons above.
  const expiresAt = state.status === "held" ? state.hold.expiresAt : null;
  const heldStart = state.status === "held" ? state.hold.startTime : null;

  useEffect(() => {
    if (!expiresAt || !heldStart) return;

    const tick = () => {
      const left = secondsUntil(expiresAt);
      if (left <= 0) {
        // Expiry is the server's call, so nothing is released here — the hold
        // is already gone on their side. Product's rule: preserve the client's
        // completed details, clear ONLY the time, and return them to time
        // selection.
        activeHold.current = null;
        setState({ status: "expired", startTime: heldStart });
        return;
      }
      setState((current) =>
        current.status === "held" ? { ...current, secondsLeft: left } : current
      );
    };

    tick();
    const timer = window.setInterval(tick, 1000);
    // A locked phone stops firing intervals entirely, so the countdown is also
    // recomputed the moment the tab comes back rather than resuming stale.
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [expiresAt, heldStart]);

  // Leaving the flow releases the hold rather than leaving a slot blocked for
  // ten minutes. Expiry would catch it eventually; being explicit means the
  // next person can book it now.
  useEffect(() => {
    return () => {
      requestVersion.current += 1;
      const hold = activeHold.current;
      if (!hold) return;
      activeHold.current = null;
      void releaseSlot(hold.id);
    };
  }, [releaseSlot]);

  /**
   * Drop a hold that is no longer ours WITHOUT asking Backend to release it.
   * Used when the signed-in uid is no longer the hold's owner (a sign-in in
   * another tab, or a switch that raced a new hold): a release would carry
   * the new uid's token and be refused, and the old owner's hold expires on
   * Backend's clock. Nothing may be committed against it from here.
   */
  const forget = useCallback(() => {
    requestVersion.current += 1;
    activeHold.current = null;
    setState({ status: "none" });
  }, []);

  /**
   * Resolves once no release is on the wire. A guest identity is only retired
   * after this (auth/guestRotation.ts), because a release signed after the
   * rotation would carry no token and leave the slot blocked until expiry.
   * Deliberately NOT waiting for acquisitions: those wait for a rotation.
   */
  const settled = useCallback(async () => {
    const releasing = pendingRelease.current;
    if (releasing) await releasing;
  }, []);

  return { state, acquire, release, forget, settled };
}
